import { useEffect, useState } from 'react';
import {
  ApiError,
  disableMemberWatch,
  fetchMembers,
  sendMemberTestEmail,
  setMemberStatus,
  type AuthUser,
  type Member,
} from '../api';
import type { EmailQuota, QueueEntry } from '../api';
import { formatDate, formatDateTime } from '../lib/format';
import type { Meta } from '../types';

interface MembersViewProps {
  currentUser: AuthUser;
  meta: Meta | undefined;
}

/**
 * När bevakningen ger noll träffar säger tratten var uppdragen försvann. Utan
 * den blir "jag får inga mejl" en gissningslek mellan kön, områdena,
 * teknikvalen och orten.
 */
function describeWatchStatus(watch: NonNullable<Member['watch']>): string {
  const status = watch.status;
  if (!status) return '';
  if (status.matches > 0) {
    const place = status.queuePosition ? `, plats ${status.queuePosition} i kön` : '';
    return `${status.matches} nya uppdrag väntar på utskick${place}.`;
  }

  const { newSinceCutoff, inWatchedAreas } = status.funnel;
  if (newSinceCutoff === 0) return 'Inga nya uppdrag sedan senaste mejlet.';
  // Teknikerna nämns bara för den som valt några – annars pekas ett filter ut
  // som inte finns, och man letar efter fel sak.
  const scope = Object.values(watch.technologies ?? {}).some((list) => (list ?? []).length > 0)
    ? 'valda områden och tekniker'
    : 'valda områden';
  if (inWatchedAreas === 0) return `${newSinceCutoff} nya uppdrag, men inget inom ${scope}.`;
  return `${newSinceCutoff} nya uppdrag, ${inWatchedAreas} inom områdena – alla bortfiltrerade av ortvalet.`;
}

function formatWait(seconds: number): string {
  return seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${Math.max(1, seconds)} sekunder`;
}

function watchTechSummary(technologies: NonNullable<Member['watch']>['technologies']): string {
  const narrowed = Object.values(technologies ?? {}).filter((list) => (list ?? []).length > 0).length;
  return narrowed === 0 ? 'alla tekniker' : `teknikval i ${narrowed} ${narrowed === 1 ? 'område' : 'områden'}`;
}

const STATUS_LABELS: Record<Member['status'], string> = {
  pending: 'Väntar på godkännande',
  active: 'Aktiv',
  disabled: 'Avstängd',
};

/**
 * Medlemshantering för administratörer. Nya konton är vilande tills någon här
 * släpper in dem – annars räcker det att känna till en xlent.se-adress för att
 * komma åt hela marknadsbilden.
 */
export function MembersView({ currentUser, meta }: MembersViewProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [testingId, setTestingId] = useState<string>();
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; message: string }>();
  const [quota, setQuota] = useState<EmailQuota>();
  const [queue, setQueue] = useState<QueueEntry[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchMembers(controller.signal)
      .then((result) => {
        setMembers(result.members);
        setQuota(result.emailQuota);
        setQueue(result.queue ?? []);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const areaLabel = (id: string) => meta?.areas.find((area) => area.id === id)?.name ?? id;

  const disableWatch = async (member: Member) => {
    setBusyId(member.id);
    setError(undefined);
    try {
      const result = await disableMemberWatch(member.id);
      setMembers((current) => current.map((m) => (m.id === member.id ? result.member : m)));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Bevakningen kunde inte stängas av.');
    } finally {
      setBusyId(undefined);
    }
  };

  const testEmail = async (member: Member) => {
    setTestingId(member.id);
    setTestResult(undefined);
    try {
      const result = await sendMemberTestEmail(member.id);
      setTestResult({ id: member.id, ok: true, message: `Skickat till ${result.email}.` });
      // Ett testmejl äter av samma kvot som bevakningarna, så siffran ska inte
      // stå kvar och ljuga tills sidan laddas om.
      setQuota((current) =>
        current
          ? { ...current, lastMinute: current.lastMinute + 1, lastHour: current.lastHour + 1 }
          : current,
      );
    } catch (cause) {
      setTestResult({
        id: member.id,
        ok: false,
        message: cause instanceof ApiError ? cause.message : 'Testmejlet kunde inte skickas.',
      });
    } finally {
      setTestingId(undefined);
    }
  };

  const change = async (member: Member, status: Member['status']) => {
    setBusyId(member.id);
    setError(undefined);
    try {
      await setMemberStatus(member.id, status);
      setMembers((current) => current.map((m) => (m.id === member.id ? { ...m, status } : m)));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Statusen kunde inte ändras.');
    } finally {
      setBusyId(undefined);
    }
  };

  if (loading) return <div className="skeleton" style={{ height: 260 }} />;

  const pending = members.filter((member) => member.status === 'pending');

  return (
    <section className="panel">
      <div className="panel__header">
        <h2 className="panel__title">Medlemmar</h2>
        <span className="panel__tools">{members.length} konton</span>
      </div>
      <p className="panel__subtitle">
        Nya konton är vilande tills du godkänner dem. En avstängd medlem loggas ut direkt och slutar få bevakningsmejl. Du kan stänga av någons bevakning men inte slå på den – ingen ska kunna anmälas till mejl de inte bett om. Testa mejl skickar ett testutskick direkt till medlemmens adress, så att du kan se att e-posten fungerar utan att vänta på nästa insamling.
      </p>

      {quota ? (
        <div
          className="notice"
          style={{ borderLeftColor: quota.allowed ? 'var(--border-strong)' : 'var(--status-warning)' }}
        >
          <span aria-hidden="true">{quota.allowed ? '✉' : '⏳'}</span>
          <span>
            <strong>E-postkvot:</strong> {quota.lastMinute} av {quota.maxPerMinute} senaste minuten,{' '}
            {quota.lastHour} av {quota.maxPerHour} senaste timmen.{' '}
            {quota.allowed
              ? 'Utskick går igenom.'
              : `Kvoten är nådd – nästa utskick möjligt om ${formatWait(quota.waitSeconds)}. Bevakningar som inte får plats skickas vid nästa insamling.`}
          </span>
        </div>
      ) : null}

      <div className="watch-queue">
        <div className="watch-queue__head">
          <strong>Bevakningskö</strong>
          <span className="panel__tools">
            {queue.length === 0
              ? 'Tom just nu – ingen har nya uppdrag att få.'
              : `${queue.length} ${queue.length === 1 ? 'mottagare väntar' : 'mottagare väntar'}, den som väntat längst först.`}
          </span>
        </div>
        {queue.length > 0 ? (
          <ol className="watch-queue__list">
            {queue.map((entry) => (
              <li key={entry.email}>
                <span className="watch-queue__pos">{entry.position}</span>
                <span>{entry.email}</span>
                <span className="panel__tools">
                  {entry.matches} {entry.matches === 1 ? 'uppdrag' : 'uppdrag'} · väntat sedan{' '}
                  {formatDateTime(entry.waitingSince)}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
        <p className="panel__tools" style={{ marginTop: 8 }}>
          Kön byggs om vid varje insamling. Den som får sitt mejl hamnar sist; den som inte fick plats i kvoten står
          kvar överst till nästa körning.
        </p>
      </div>

      {error ? (
        <div className="notice">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </div>
      ) : null}

      {pending.length > 0 ? (
        <div className="notice" style={{ borderLeftColor: 'var(--accent)' }}>
          <span aria-hidden="true">●</span>
          <span>
            {pending.length} {pending.length === 1 ? 'konto väntar' : 'konton väntar'} på ditt godkännande.
          </span>
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Medlem</th>
              <th style={{ width: 150 }}>Status</th>
              <th style={{ width: 110 }}>Registrerad</th>
              <th style={{ width: 150 }}>Senast inloggad</th>
              <th style={{ width: 210 }}>Bevakning</th>
              <th style={{ width: 210 }}>
                <span className="visually-hidden">Åtgärder</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const isSelf = member.id === currentUser.id;
              return (
                <tr key={member.id}>
                  <td>
                    <strong>{member.displayName}</strong>
                    {member.role === 'admin' ? <span className="tag" style={{ marginLeft: 6 }}>Admin</span> : null}
                    {isSelf ? <span className="tag" style={{ marginLeft: 6 }}>Du</span> : null}
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{member.email}</div>
                  </td>
                  <td>
                    <span className={`status status--${member.status}`}>{STATUS_LABELS[member.status]}</span>
                    {member.approvedBy ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                        av {member.approvedBy}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{formatDate(member.createdAt)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {member.lastLoginAt ? formatDateTime(member.lastLoginAt) : 'aldrig'}
                  </td>
                  <td>
                    {member.watch && member.watch.areas.length > 0 ? (
                      <>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {member.watch.areas.map((area) => areaLabel(area)).join(', ')}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {member.watch.locations?.length ? member.watch.locations.join(', ') : 'alla orter'}
                          {' · '}
                          {watchTechSummary(member.watch.technologies)}
                        </div>
                        {member.watch.status ? (
                          <div style={{ fontSize: 11, marginTop: 3 }}>{describeWatchStatus(member.watch)}</div>
                        ) : null}
                        {member.watch.active ? (
                          <button
                            type="button"
                            className="chip chip--mini"
                            style={{ marginTop: 5 }}
                            disabled={busyId === member.id}
                            onClick={() => disableWatch(member)}
                            title="Stoppar utskicken. Medlemmen kan själv starta om sin bevakning."
                          >
                            {busyId === member.id ? '…' : 'Stäng av bevakning'}
                          </button>
                        ) : (
                          <span className="status status--disabled" style={{ marginTop: 5, display: 'inline-block' }}>
                            Avstängd
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Ingen</span>
                    )}
                  </td>
                  <td>
                    {member.status !== 'active' ? (
                      <button
                        type="button"
                        className="chip chip--mini chip--active"
                        disabled={busyId === member.id}
                        onClick={() => change(member, 'active')}
                      >
                        {busyId === member.id ? '…' : 'Godkänn'}
                      </button>
                    ) : null}
                    {member.status === 'active' && !isSelf ? (
                      <button
                        type="button"
                        className="chip chip--mini"
                        disabled={busyId === member.id}
                        onClick={() => change(member, 'disabled')}
                      >
                        {busyId === member.id ? '…' : 'Stäng av'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="chip chip--mini"
                      disabled={testingId === member.id}
                      onClick={() => testEmail(member)}
                      title={`Skickar ett testmejl till ${member.email} direkt.`}
                    >
                      {testingId === member.id ? 'Skickar…' : 'Testa mejl'}
                    </button>
                    {testResult?.id === member.id ? (
                      <div
                        style={{
                          fontSize: 11.5,
                          marginTop: 5,
                          color: testResult.ok ? 'var(--status-good)' : 'var(--status-critical)',
                        }}
                      >
                        {testResult.message}
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
